import { isDefined } from "@prismicio/editor-support/Type"
import clsx from "clsx"
import { type ReactNode, useState } from "react"

import styles from "./FileDropZone.module.css"
import { isValidFileExtension, type MediaAssetType } from "./FileUpload.utils"

export interface FileDropZoneProps {
  onFilesSelected: (files: File[]) => void
  overlay: ReactNode
  assetType?: MediaAssetType
  children?: ReactNode
}

export function FileDropZone(props: FileDropZoneProps) {
  const { onFilesSelected, overlay, children, assetType = "all" } = props

  const [isDropTarget, setIsDropTarget] = useState(false)

  function validDropOperation(event: React.DragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.items) return false
    if (assetType === "all") return true

    // Check if file type matches a valid asset type
    const hasMatch = [...event.dataTransfer.items].every((item) =>
      isValidFileExtension(item.type, assetType)
    )

    return hasMatch
  }

  function onDragEnter(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const isValidDropOperation = validDropOperation(event)
    setIsDropTarget(isValidDropOperation)
  }

  function onDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const isValidDropOperation = validDropOperation(event)
    setIsDropTarget(isValidDropOperation)
  }

  function onDragLeave(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setIsDropTarget(false)
  }

  async function onDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const isValidDropOperation = validDropOperation(event)

    if (!event.dataTransfer.items || !isValidDropOperation) return
    const fileDropItems = [...event.dataTransfer.items].filter(
      (item) =>
        item.kind === "file" &&
        (assetType === "all"
          ? true
          : isValidFileExtension(item.type, assetType))
    )
    const newFiles = (
      await Promise.all(fileDropItems.map((item) => item.getAsFile()))
    ).filter(isDefined)

    setIsDropTarget(false)
    onFilesSelected(newFiles)
  }

  return (
    <div className={clsx(styles.root, isDropTarget && styles.dropTarget)}>
      <div
        aria-label="dropzone"
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={styles.dropzone}
      >
        {children}
      </div>
      {isDropTarget && (
        <div className={styles.overlayContainer}>
          <div className={styles.overlay}>{overlay}</div>
        </div>
      )}
    </div>
  )
}
